import {
    Button,
    IconButton,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Radio,
    RadioGroup,
    FormControlLabel,
    Typography,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faSave,
    faTimesCircle,
    faArrowAltCircleDown,
    faSortNumericDown,
} from '@fortawesome/pro-solid-svg-icons';
import { Close } from '@material-ui/icons';
import {
    ShiftlineSelectionModals,
    EmployeeAutoNumberingType,
} from '../../Enums';
import './ShiftlineSelectionConfirmation.scss';

/**
 * @param {{
 *  autoNumberingType: string,
 *  value: string,
 *  label: string,
 *  handleOnClick: Function,
 *  subtitleText: string,
 * }} param0
 */
const StyledRadio = ({
    autoNumberingType, value, label, handleOnClick, subtitleText, ...other
}) => (
    <div
        className={`ssc-selection-modal-option ${autoNumberingType === value ? 'order-type-selected' : ''}`}
        onClick={() => handleOnClick()}
    >
        <FormControlLabel
            control={<Radio color="primary" />}
            value={value}
            label={label}
            {...other}
        />
        <Typography
            variant="subtitle1"
        >
            {subtitleText}
        </Typography>
    </div>
);

/**
 * @param {{
 *  topSvgIcon: import('@fortawesome/fontawesome-svg-core').IconProp,
 *  onClose: import('react').MouseEventHandler<HTMLButtonElement>,
 *  titleLabel: string,
 *  iconContainerClassName: string,
 * }} param0
 */
const StyledDialogTitle = ({
    topSvgIcon, onClose, titleLabel, iconContainerClassName, ...rest
}) => (
    <DialogTitle {...rest}>
        <div className={`ssc-selection-modal-title-icon-container ${iconContainerClassName}`}>
            <FontAwesomeIcon
                icon={topSvgIcon}
                className="icon"
                size="2x"
            />
        </div>
        <div className="ssc-selection-modal-title-close-container">
            <IconButton
                className="iconButton"
                color="inherit"
                component="span"
                onClick={onClose}
            >
                <Close />
            </IconButton>
        </div>
        <div className="ssc-selection-modal-title-label-container">
            {titleLabel}
        </div>
    </DialogTitle>
);

/**
 * @param {{
 *  buttonLabel: string,
 *  onClick: import('react').MouseEventHandler<HTMLButtonElement>,
 *  disabledButton: boolean
 * }} param0
 */
const StyledDialogActions = ({
    buttonLabel, onClick, disabledButton, ...rest
}) => (
    <DialogActions {...rest}>
        <Button
            color="primary"
            variant="contained"
            onClick={onClick}
            disabled={disabledButton}
        >
            {buttonLabel}
        </Button>
    </DialogActions>
);

/**
 * @param {{
 *  showModal: number,
 *  templateToLoadName: string,
 *  autoNumberingType: string,
 *  setShowModal: Function,
 *  handleSaveTemplate?: Function,
 *  handleLoadTemplate?: Function,
 *  handleSaveAsDraft?: Function,
 *  handleSubmit?: Function,
 *  handleDecline?: Function,
 *  setAutoNumberingType: Function,
 *  handleAutoOrder: Function,
 *  handleBack?: Function,
 *  t: any,
 * }} props
 */
function ShiftlineSelectionConfirmation(props) {
    const {
        showModal, templateToLoadName, autoNumberingType,
        setShowModal, handleSaveTemplate = () => {}, handleLoadTemplate = () => {},
        handleSaveAsDraft = () => {}, handleSubmit = () => {}, handleDecline = () => {},
        setAutoNumberingType, handleAutoOrder, handleBack = () => {},
        t,
    } = props;
    const getModalContent = () => {
        switch (showModal) {
            case ShiftlineSelectionModals.SaveTemplate:
                return (
                    <>
                        <StyledDialogTitle
                            topSvgIcon={faSave}
                            onClose={() => setShowModal(ShiftlineSelectionModals.None)}
                            titleLabel={t('modal.saveAsTemplate.title')}
                            iconContainerClassName="ssc-selection-modal-title-icon-container-draft"
                        />
                        <DialogContent dividers>
                            <DialogContentText>
                                {t('modal.saveAsTemplate.content')}
                            </DialogContentText>
                        </DialogContent>
                        <StyledDialogActions
                            buttonLabel="Confirm"
                            onClick={() => handleSaveTemplate()}
                            disabledButton={false}
                        />
                    </>
                );
            case ShiftlineSelectionModals.LoadTemplate:
                return (
                    <>
                        <StyledDialogTitle
                            topSvgIcon={faArrowAltCircleDown}
                            onClose={() => setShowModal(ShiftlineSelectionModals.None)}
                            titleLabel={t('modal.loadFromTemplate.title')}
                            iconContainerClassName="ssc-selection-modal-title-icon-container-draft"
                        />
                        <DialogContent dividers>
                            <DialogContentText>
                                {templateToLoadName}
                            </DialogContentText>
                        </DialogContent>
                        <StyledDialogActions
                            buttonLabel="Confirm"
                            onClick={() => handleLoadTemplate()}
                            disabledButton={false}
                        />
                    </>
                );
            case ShiftlineSelectionModals.Close:
                return (
                    <>
                        <StyledDialogTitle
                            topSvgIcon={faSave}
                            onClose={() => setShowModal(ShiftlineSelectionModals.None)}
                            titleLabel={t('modal.saveAsDraft.title')}
                            iconContainerClassName="ssc-selection-modal-title-icon-container-draft"
                        />

                        <DialogContent dividers>
                            <DialogContentText>
                                {t('modal.saveAsDraft.content')}
                            </DialogContentText>
                        </DialogContent>
                        <StyledDialogActions
                            buttonLabel="Confirm"
                            onClick={() => handleSaveAsDraft()}
                            disabledButton={false}
                        />
                    </>
                );
            case ShiftlineSelectionModals.ConfirmBidSelection:
                return (
                    <>
                        <StyledDialogTitle
                            topSvgIcon={faCheck}
                            onClose={() => setShowModal(ShiftlineSelectionModals.None)}
                            titleLabel={t('modal.confirmBid.title')}
                            iconContainerClassName="ssc-selection-modal-title-icon-container-submit"
                        />

                        <DialogContent dividers>
                            <DialogContentText>
                                {t('modal.confirmBid.content')}
                            </DialogContentText>
                        </DialogContent>
                        <StyledDialogActions
                            buttonLabel="Confirm"
                            onClick={() => handleSubmit()}
                            disabledButton={false}
                        />
                    </>
                );
            case ShiftlineSelectionModals.DeclineBid:
                return (
                    <>
                        <StyledDialogTitle
                            topSvgIcon={faTimesCircle}
                            onClose={() => setShowModal(ShiftlineSelectionModals.None)}
                            titleLabel={t('modal.declineBid.title')}
                            iconContainerClassName="ssc-selection-modal-title-icon-container-decline"
                        />

                        <DialogContent dividers>
                            <DialogContentText>
                                {t('modal.declineBid.content')}
                            </DialogContentText>
                        </DialogContent>
                        <StyledDialogActions
                            buttonLabel="Confirm"
                            onClick={() => handleDecline()}
                            disabledButton={false}
                        />
                    </>
                );
            case ShiftlineSelectionModals.AutoNumbering:
                return (
                    <>
                        <StyledDialogTitle
                            topSvgIcon={faSortNumericDown}
                            onClose={() => setShowModal(ShiftlineSelectionModals.None)}
                            titleLabel={t('modal.autoOrder.title')}
                            iconContainerClassName="ssc-selection-modal-title-icon-container-draft"
                        />
                        <DialogContent dividers>
                            <DialogContentText>
                                {t('modal.autoOrder.content')}
                            </DialogContentText>
                            <RadioGroup
                                name="auto-order-type"
                                value={autoNumberingType}
                                onChange={(event) => setAutoNumberingType(event.target.value)}
                            >
                                <StyledRadio
                                    subtitleText={t('modal.autoOrder.options.add.subtitle')}
                                    value={EmployeeAutoNumberingType.Add}
                                    label={t('modal.autoOrder.options.add.label')}
                                    autoNumberingType={autoNumberingType}
                                    handleOnClick={() => setAutoNumberingType(EmployeeAutoNumberingType.Add)}
                                />
                                <StyledRadio
                                    subtitleText={t('modal.autoOrder.options.overwrite.subtitle')}
                                    value={EmployeeAutoNumberingType.Overwrite}
                                    label={t('modal.autoOrder.options.overwrite.label')}
                                    autoNumberingType={autoNumberingType}
                                    handleOnClick={() => setAutoNumberingType(EmployeeAutoNumberingType.Overwrite)}
                                />
                            </RadioGroup>
                        </DialogContent>
                        <StyledDialogActions
                            buttonLabel="Confirm"
                            onClick={() => handleAutoOrder()}
                            disabledButton={!autoNumberingType}
                        />
                    </>
                );
            case ShiftlineSelectionModals.Back:
                return (
                    <>
                        <DialogTitle>{t('modal.onBlockBack.areYouSure')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {t('modal.onBlockBack.youWillLose')}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => setShowModal(ShiftlineSelectionModals.None)}
                            >
                                {t('common.no')}
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleBack()}
                            >
                                {t('common.yes')}
                            </Button>
                        </DialogActions>
                    </>
                );
            case ShiftlineSelectionModals.BackOnBehalf:
                return (
                    <>
                        <DialogTitle>{t('modal.onBlockBackOnBehalf.areYouSure')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {t('modal.onBlockBackOnBehalf.youWillLose')}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => setShowModal(ShiftlineSelectionModals.None)}
                            >
                                {t('common.no')}
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleBack()}
                            >
                                {t('common.yes')}
                            </Button>
                        </DialogActions>
                    </>
                );
            default:
                return null;
        }
    };
    return (
        getModalContent()
    );
}

export default ShiftlineSelectionConfirmation;
